import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IAuthState, IThunkAPI, IUser } from '@/store/types';
import {
  IClient,
  ILxProfile,
  INewPasswordRequest,
  IResetTokenRequest,
  IResetTokenVerify,
  IResetUsernameRequest,
} from '@/api/types';
import type { IdHueMap } from '@/types/styles';
import { isFulfilledAction, isPendingAction, isRejectedAction, logError, mapContactColors } from '@/helpers';
import { profileConverter } from '@/helpers/converter/profileConverter';
import { AuthStatus, UserType } from '@/store/enums';
import { userProfileApi } from '@/api/pub/userProfile';
import { RootState } from '..';

const initialState: Readonly<IAuthState> = Object.freeze({
  user: null,
  menuType: UserType.unauthorized,
  contractColors: null,
  displayLabels: [],
  isError: false,
  isSuccess: false,
  isFetching: false,
  hasSentPasswordResetLink: false,
  errorMessage: '',
  authStatus: AuthStatus.newSession,
  application: 'Scheduling',
  dontShowArchived: false,
});

export const sendResetLink = createAsyncThunk<unknown, IResetUsernameRequest, IThunkAPI>(
  'auth/reset-password/generate',
  async (payload, thunkAPI) => {
    try {
      const { username } = payload;
      const response = await userProfileApi.sendResetPasswordLink(username);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const verifyResetLink = createAsyncThunk<IResetTokenVerify, IResetTokenRequest, IThunkAPI>(
  'auth/reset-password/verify',
  async (options, thunkAPI) => {
    try {
      const { resetToken } = options;
      const response = await userProfileApi.verifyResetLink(resetToken);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const resetPassword = createAsyncThunk<unknown, INewPasswordRequest, IThunkAPI>(
  'auth/reset-password',
  async (options, thunkAPI) => {
    try {
      const response = await userProfileApi.resetPassword(options);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getProfile = createAsyncThunk<IUser | null, void, IThunkAPI>('auth/getProfile', async (_, thunkAPI) => {
  try {
    const response = await userProfileApi.getProfile();

    if (!response.data.isAuthenticated) return null;

    const user = profileConverter(JSON.parse(response.data.lxProfile));
    return user;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getUpdatedProfile = createAsyncThunk<IUser, void, IThunkAPI>(
  'get/updated/profile',
  async (_, thunkAPI) => {
    try {
      const response = await userProfileApi.getUpdatedProfile();
      const user: IUser = profileConverter(response.data as ILxProfile);
      return user;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const authenticateUser = createAsyncThunk<boolean, void, IThunkAPI>('auth/getuser', async (_, thunkAPI) => {
  try {
    const response = await userProfileApi.checkUserIdentity();
    if (!response.data.isAuthenticated) {
      sessionStorage.clear();
      await userProfileApi.logout();
      return false;
    }
    return response.data.isAuthenticated;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const logout = createAsyncThunk<void, void, IThunkAPI>('auth/logout', async (_, thunkAPI) => {
  try {
    await userProfileApi.logout();
    return;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const setCurrentClient = createAsyncThunk<ILxProfile, number, IThunkAPI>(
  'pub/userProfile/setCurrentClient',
  async (clientId, thunkAPI) => {
    try {
      const response = await userProfileApi.updateCurrentClient(clientId);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const changeMenuType = createAsyncThunk<ILxProfile, UserType, IThunkAPI>(
  'pub/changeMenuType',
  async (menuType, thunkAPI) => {
    try {
      const response = await userProfileApi.updateMenuType(menuType);
      return response.data as ILxProfile;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

// needed to manual debug and test
const updateTimezone = createAction<string>('auth/updateTimezone');

export const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth(state) {
      sessionStorage.clear();
      state.user = initialState.user;
      state.authStatus = initialState.authStatus;
      localStorage.removeItem('isLoggedIn');
    },
    clearState(state) {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.hasSentPasswordResetLink = false;
      state.errorMessage = '';
    },
    setDontShowArchived(state, action: PayloadAction<boolean>) {
      state.dontShowArchived = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sendResetLink.fulfilled, state => {
        state.hasSentPasswordResetLink = true;
      })
      .addCase(sendResetLink.rejected, state => {
        state.hasSentPasswordResetLink = false;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.authStatus = AuthStatus.loggedIn;
        state.user = action.payload;
        state.menuType = action.payload.menuType;
        fillStateObject(state, action.payload);
        state.contractColors = populateContractsAndColor(action.payload);
      })
      .addCase(getUpdatedProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        fillStateObject(state, action.payload);
        state.contractColors = populateContractsAndColor(action.payload);
      })
      .addCase(logout.fulfilled, state => {
        state.user = null;
        state.menuType = UserType.unauthorized;
        state.displayLabels = [];
        state.authStatus = AuthStatus.loggedOut;
        localStorage.clear();
        sessionStorage.clear();
      })
      .addCase(updateTimezone, (state, action) => {
        if (state.user) {
          state.user.timeZoneName = action.payload;
        }
      })
      .addCase(setCurrentClient.fulfilled, (state, action) => {
        if (state.user) {
          state.user.currentClient = action.payload.currentClient;
          state.user.userPermissions = action.payload.userPermissions;
        }
      })
      .addCase(changeMenuType.fulfilled, (state, action) => {
        state.menuType = action.payload.menuType;
        if (state.user) {
          state.user.userPermissions = action.payload.userPermissions;
        }
      })
      .addMatcher(isFulfilledAction, state => {
        state.isSuccess = true;
        state.isError = false;
        state.isFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isError = true;
        state.isSuccess = false;
        state.isFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isFetching = true;
      });
  },
});

const populateContractsAndColor = ({ agentContracts }: IUser): IdHueMap | null => {
  if (!agentContracts) return null;
  return mapContactColors(agentContracts.sort((a, b) => a.contractId - b.contractId));
};

const fillStateObject = (state: IAuthState, user: IUser) => {
  if (state.user !== null) {
    state.user.userPermissions = user.userPermissions;
    state.user.agentContractClients = user.agentContractClients;
    state.user.agentContractJobs = user.agentContractJobs;
    state.user.agentContractSchedules = user.agentContractSchedules;
    state.user.agentContracts = user.agentContracts;
  }
  state.displayLabels = user.displayLabels;
};

export const selectedClient = (state: RootState): IClient | null | undefined => {
  return state.auth.user?.currentClient;
};

// actions
export const { clearAuth, clearState, setDontShowArchived } = slice.actions;

export default slice.reducer;
